import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import SEO from "@/components/SEO";

/**
 * Verify Email screen.
 *
 * States (H-1 launch-audit fix):
 *   verifying    — token in URL, verification API call in flight
 *   ok           — verification succeeded → auto-redirect to onboarding
 *   no_token     — page opened directly (no ?token=). Neutral copy;
 *                  NEVER shows "Verification failed / Something went wrong".
 *   invalid      — server rejected the token (expired / already used /
 *                  malformed). Clear, non-scary explanation.
 *   error        — actual server / network failure. Rare.
 */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { refresh } = useCollectorAuth();
  const [state, setState] = useState(token ? "verifying" : "no_token");
  const [publicId, setPublicId] = useState(null);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    // No token → don't hit the API, don't show a failure.
    if (!token) return;
    api.post("/collector-auth/verify-email", { token })
      .then(async (r) => {
        setPublicId(r.data.public_collector_id);
        setState("ok");
        // Phase C1 — the verify-email response auto-logs in the caller
        // via HttpOnly cookies. Refresh the local context so we can
        // read `pending_claim_cert_number` and decide whether to
        // launch onboarding as a normal registration OR as a
        // certificate-claim continuation.
        await refresh();
        let entryType = "registration";
        let entryCertNumber = null;
        try {
          const me = await api.get("/collector-auth/me");
          const pending = me?.data?.collector?.pending_claim_cert_number || null;
          if (pending && /^[A-Z]{2}-\d{4}-\d{4}$/.test(pending)) {
            entryType = "certificate_claim";
            entryCertNumber = pending;
          }
        } catch (_) { /* fall back to plain registration */ }
        try {
          await api.post("/me/onboarding/start", {
            entry_type: entryType,
            ...(entryCertNumber ? { entry_cert_number: entryCertNumber } : {}),
          });
        } catch (_) { /* idempotent */ }
        await refresh();
        setTimeout(() => navigate("/onboarding/profile", { replace: true }), 800);
      })
      .catch((err) => {
        const status = err.response?.status;
        const detail = err.response?.data?.detail || "";
        // Any 4xx from the endpoint = invalid or expired token — a clear
        // customer-visible state. 5xx / network = genuine error.
        if (status && status >= 400 && status < 500) {
          setState("invalid");
          setErrMsg(detail || "This verification link is invalid or has expired.");
        } else {
          setState("error");
          setErrMsg("We couldn't reach the verification service. Please try again in a moment.");
        }
      });
  }, [token, refresh, navigate]);

  return (
    <div className="container-gm py-16 max-w-md">
      <SEO title="Verify email" />
      <div className="rounded-3xl border-2 border-[var(--ink)] bg-white p-8 shadow-[10px_10px_0_var(--panini-red)]" data-testid="verify-email-panel">
        {state === "verifying" && (
          <div data-testid="verify-email-verifying">
            <div className="kicker kicker-black">Verifying…</div>
            <p className="mt-3 text-[var(--smoke)]">Hold on a second while we confirm your email.</p>
          </div>
        )}
        {state === "ok" && (
          <div data-testid="verify-email-success">
            <div className="kicker kicker-yellow">Verified</div>
            <h1 className="h-display text-3xl mt-3">Welcome to CollectorUp</h1>
            <p className="mt-4 text-[var(--smoke)]">Your permanent Public Collector ID is</p>
            <div className="mt-2 font-mono text-2xl font-bold text-[var(--panini-red)]" data-testid="verify-public-id">{publicId}</div>
            <p className="mt-4 text-[var(--smoke)] text-sm">You&apos;re signed in.</p>
            <button onClick={() => navigate("/onboarding/profile")} className="btn-red text-sm mt-6" data-testid="verify-goto-dashboard">Set up your profile</button>
          </div>
        )}
        {state === "no_token" && (
          <div data-testid="verify-email-no-token">
            <div className="kicker kicker-black">Check your inbox</div>
            <h1 className="h-display text-3xl mt-3">Open your verification link</h1>
            <p className="mt-4 text-[var(--smoke)] text-sm leading-relaxed">
              To verify your email, please use the link we sent to your inbox.
              If you can&apos;t find it, check your spam folder or request a new
              verification email after signing in.
            </p>
            <div className="mt-6 flex gap-3 flex-wrap">
              <Link to="/login" className="btn-red text-sm" data-testid="verify-goto-login">Go to Sign in</Link>
              <Link to="/" className="btn-ghost text-sm" data-testid="verify-goto-home">Return home</Link>
            </div>
          </div>
        )}
        {state === "invalid" && (
          <div data-testid="verify-email-invalid">
            <div className="kicker kicker-red">Link no longer valid</div>
            <h1 className="h-display text-3xl mt-3">This verification link is invalid or has expired</h1>
            <p className="mt-4 text-[var(--smoke)] text-sm leading-relaxed">
              {errMsg} Sign in to request a fresh verification email.
            </p>
            <div className="mt-6 flex gap-3 flex-wrap">
              <Link to="/login" className="btn-red text-sm" data-testid="verify-invalid-goto-login">Sign in</Link>
              <Link to="/register" className="btn-ghost text-sm">Create account</Link>
            </div>
          </div>
        )}
        {state === "error" && (
          <div data-testid="verify-email-error">
            <div className="kicker kicker-red">Something went wrong</div>
            <h1 className="h-display text-3xl mt-3">We couldn&apos;t reach the verification service</h1>
            <p className="mt-4 text-[var(--smoke)]">{errMsg}</p>
            <div className="mt-6 flex gap-3 flex-wrap">
              <button onClick={() => window.location.reload()} className="btn-red text-sm" data-testid="verify-error-retry">Try again</button>
              <Link to="/login" className="btn-ghost text-sm">Sign in</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
